<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import { Link } from '@inertiajs/svelte';
    import { show } from '@/routes/cases';
    import { formatDate as fmtDate } from '../utils/dates';

    interface Result {
        id: number;
        question_body: string;
        question_type: string;
        response_text: string | null;
        selected_option_body: string | null;
        is_correct: boolean | null;
        revealed: boolean;
        scored_at: string | null;
        submitted_at: string;
        schedule_date: string;
        schedule_sqid: string;
        case_topic: string | null;
        case_institution: string | null;
    }

    interface Summary {
        total: number;
        correct: number;
        incorrect: number;
        pending: number;
    }

    let { results, summary }: { results: Result[]; summary: Summary } = $props();

    function caseLink(r: Result) {
        const [year, month, day] = r.schedule_date.split('-');
        return show({ year: +year, month: +month, day: +day, sqid: r.schedule_sqid });
    }

    function formatDate(dateStr: string): string {
        return fmtDate(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
    }

    const grouped = $derived(
        results.reduce<Record<string, Result[]>>((acc, r) => {
            (acc[r.schedule_date] ??= []).push(r);
            return acc;
        }, {})
    );

    const sortedDates = $derived(Object.keys(grouped).sort((a, b) => b.localeCompare(a)));
</script>

<svelte:head>
    <title>My Results — Case of the Day</title>
</svelte:head>

<div class="max-w-3xl mx-auto space-y-6">
        <h1 class="text-2xl font-bold text-gray-100">My Results</h1>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-4 text-center">
                <p class="text-2xl font-bold text-gray-100">{summary.total}</p>
                <p class="text-sm text-gray-500">Answered</p>
            </div>
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-4 text-center">
                <p class="text-2xl font-bold text-green-400">{summary.correct}</p>
                <p class="text-sm text-gray-500">Correct</p>
            </div>
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-4 text-center">
                <p class="text-2xl font-bold text-red-400">{summary.incorrect}</p>
                <p class="text-sm text-gray-500">Incorrect</p>
            </div>
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-4 text-center">
                <p class="text-2xl font-bold text-cyan-400">{summary.pending}</p>
                <p class="text-sm text-gray-500">Pending</p>
            </div>
        </div>

        {#if results.length === 0}
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-8 text-center">
                <p class="text-gray-500">You haven't answered any cases yet.</p>
            </div>
        {:else}
            {#each sortedDates as date (date)}
                <div>
                    <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">{formatDate(date)}</h2>
                    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 divide-y divide-gray-800">
                        {#each grouped[date] as r (r.id)}
                            <div class="p-4 flex items-start gap-3">
                                <span class="mt-0.5 shrink-0">
                                    {#if !r.revealed}
                                        <span class="inline-flex items-center justify-center size-7 rounded-full bg-cyan-900 text-cyan-300" title="Awaiting reveal">
                                            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".7" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zM232.1 117.5c.1-.8 .2-1.6 .4-2.4 .3-1.6 .8-3.1 1.4-4.5 1.2-2.9 3-5.5 5.1-7.6 4.3-4.3 10.3-7 17-7 13.3 0 24 10.7 24 24l0 123.2c28.4 19 56.9 37.9 85.3 56.9 11 7.4 14 22.2 6.7 33.3s-22.3 14-33.3 6.7c-32-21.3-64-42.7-96-64-3.3-2.2-6-5.2-7.8-8.6-.9-1.7-1.6-3.5-2.1-5.4-.2-1-.4-1.9-.5-2.9-.1-.5-.1-1-.1-1.5s0-.9 0-1.5c0-45.3 0-90.7 0-136.3 0-.6 0-1.4 .1-2.2z"/><path fill="currentColor" d="M256 96c-13.3 0-24 10.7-24 24l0 136c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2 280 120c0-13.3-10.7-24-24-24z"/></svg>
                                        </span>
                                    {:else if r.is_correct === true}
                                        <span class="inline-flex items-center justify-center size-7 rounded-full bg-green-900 text-green-300">
                                            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path opacity=".7" fill="currentColor" d="M434.8 70.1c14.3 10.4 17.5 30.4 7.1 44.7l-256 352c-5.5 7.6-14 12.3-23.4 13.1s-18.5-2.7-25.1-9.3l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l101.5 101.5 234-321.7c10.4-14.3 30.4-17.5 44.7-7.1z"/></svg>
                                        </span>
                                    {:else if r.is_correct === false}
                                        <span class="inline-flex items-center justify-center size-7 rounded-full bg-red-900 text-red-300">
                                            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path opacity=".7" fill="currentColor" d="M55.1 73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L147.2 256 9.9 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192.5 301.3 329.9 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.8 256 375.1 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192.5 210.7 55.1 73.4z"/></svg>
                                        </span>
                                    {:else}
                                        <span class="inline-flex items-center justify-center size-7 rounded-full bg-amber-900 text-amber-300">
                                            <svg class="size-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path opacity=".7" fill="currentColor" d="M0 96L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-352c0-35.3-28.7-64-64-64l-2.7 0c1.8 5 2.7 10.4 2.7 16 0 26.5-21.5 48-48 48L112 96c-26.5 0-48-21.5-48-48 0-5.6 1-11 2.7-16L64 32C28.7 32 0 60.7 0 96z"/><path fill="currentColor" d="M64 48C64 21.5 85.5 0 112 0L272 0c26.5 0 48 21.5 48 48s-21.5 48-48 48L112 96C85.5 96 64 74.5 64 48z"/></svg>
                                        </span>
                                    {/if}
                                </span>
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2 text-xs text-gray-400 mb-1">
                                        {#if r.case_topic}
                                            <span class="font-medium text-cyan-400 uppercase tracking-wide">{r.case_topic}</span>
                                            <span>&middot;</span>
                                        {/if}
                                        <Link href={caseLink(r)} class="hover:underline">View case</Link>
                                    </div>
                                    <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                                    <p class="text-sm text-gray-200 prose prose-invert prose-sm max-w-none">{@html r.question_body}</p>
                                    <p class="text-sm text-gray-500 mt-1">
                                        Your answer: {r.response_text ?? r.selected_option_body ?? '—'}
                                    </p>
                                </div>
                            </div>
                        {/each}
                    </div>
                </div>
            {/each}
        {/if}
    </div>
